import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/react-hooks';
import * as Yup from 'yup';

import { Button, FormErrors } from '@squirrellearningltd/go-teacher-components';
import { TEACHER_JOB_ROLES } from '@squirrellearningltd/go-shared-utils';
import { getRoleLabel, usaOnlyRoles } from '../../../../../../../../services/utils';
import { GET_SCHOOL_QUERY } from '../../../../../../../../services/school/graphql';
import { ADD_TEACHER_MUTATION } from '../../services/graphql';

const AddTeacher = ({ closeModal, schoolId }) => {
    const [formErrors, setFormErrors] = useState([]);
    const schema = Yup.object().shape({
        firstName: Yup.string().required('Please enter first name e.g. Penny'),
        lastName: Yup.string().required('Please enter first name e.g. Lane'),
        displayName: Yup.string().required('Please enter a display name e.g. Mrs Lane'),
        email: Yup.string().email().required('Please enter an email address e.g. pennylane@yourschool.ac.uk'),
        roleId: Yup.string().required('Please select a role'),
    });

    const { register, handleSubmit, errors } = useForm({
        validationSchema: schema,
        mode: 'onSubmit'
    });
    
    const [addTeacher, { loading }] = useMutation(ADD_TEACHER_MUTATION, {
        update: (cache, { data }) => {
            if (data.addTeacher.errors && data.addTeacher.errors.length !== 0) {
                setFormErrors(data.addTeacher.errors);
                return;
            }
            const schoolQuery = cache.readQuery({ query: GET_SCHOOL_QUERY, variables: { schoolId } });
            if (schoolQuery) {
                cache.writeQuery({
                    query: GET_SCHOOL_QUERY,
                    variables: { schoolId },
                    data: {
                        school: {
                            ...schoolQuery.school, 
                            admins: data.addTeacher.teacher.isSchoolAdmin ? [...schoolQuery.school.admins, data.addTeacher.teacher] : schoolQuery.school.admins,
                            teachers: [...schoolQuery.school.teachers, data.addTeacher.teacher]
                        }
                    }
                });
                closeModal();
            }
        }
    })

    const onSubmit = values => {
        const { firstName, lastName, displayName, email, isSchoolAdmin, roleId } = values;
        addTeacher({
            variables: {
                firstName, 
                lastName, 
                displayName, 
                email, 
                isSchoolAdmin,
                schoolId,
                roleId: +roleId
            }
        });
    }

    return (
        <div className='site-admin__modal__content'>
            <h1 className='title u-m-base-2'>Create Teacher Account</h1>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className='basic-form__group'>
                    <input
                        name='firstName'
                        placeholder='First Name e.g. Penny'
                        className='basic-form__text-box'
                        ref={register}
                        type='text'
                    />
                </div>
                {errors.firstName && <p className='basic-form__hint'>{errors.firstName.message}</p>}
                <div className='basic-form__group'>
                    <input
                        name='lastName'
                        placeholder='Last Name e.g. Lane'
                        className='basic-form__text-box'
                        ref={register}
                        type='text'
                    />
                </div>
                {errors.lastName && <p className='basic-form__hint'>{errors.lastName.message}</p>}
                <div className='basic-form__group'>
                    <input
                        name='displayName'
                        placeholder='Display Name e.g. Mrs Lane'
                        className='basic-form__text-box'
                        ref={register}
                        type='text'
                    />
                </div>
                {errors.displayName && <p className='basic-form__hint'>{errors.displayName.message}</p>}
                <div className='basic-form__group'>
                    <input
                        name='email'
                        placeholder='Email e.g. pennylane@yourschool.ac.uk'
                        className='basic-form__text-box'
                        ref={register}
                        type='email'
                    />
                </div>
                {errors.email && <p className='basic-form__hint'>{errors.email.message}</p>}
                <div className='basic-form__group basic-form__group--check'>
                    <input
                        name='isSchoolAdmin'
                        className='basic-form__check-box'
                        ref={register}
                        type='checkbox'
                    />
                    <label className='basic-form__check-label' htmlFor='isAdmin'>Administrator</label>
                </div>

                <p>Select the teacher's role at the school</p>
                <div className='basic-form__group u-m-base-2'>
                    <select name="roleId" ref={register} className="basic-form__text-select">
                        <option value=''>Select role</option>
                        {
                            Object.entries(TEACHER_JOB_ROLES).filter(r => !usaOnlyRoles.includes(r[1])).map(role => {
                                return (<option key={`role-${role[1]}`} value={role[1]}>{getRoleLabel(role[1])}</option>)
                            })
                        }
                    </select>
                    {errors.roleId && <p className="basic-form__hint u-m-top-0">{errors.roleId.message}</p>}
                </div>
                {formErrors.length !== 0 && <FormErrors errors={formErrors} />}
                <div className='u-display-flex u-align-center'>
                    <Button type='submit' className='u-m-right-1'>{loading ? 'Saving...' : 'Create'}</Button>
                    <Button type='button' outline onClick={() => closeModal()}>Cancel</Button>
                </div>
            </form>
        </div>
    );
}

export default AddTeacher;

